<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import SlideOver from '@/components/shared/SlideOver.vue'
import type { ItemSaveResult } from '@/composables/useGallery'
import { ExternalVideoProvider, GalleryApiErrorCode } from '@/types/galleries'
import type { ExternalVideoPayload, OrganizerGalleryItem } from '@/types/galleries'
import { parseExternalVideoUrl } from '@/utils/galleries/externalVideoUrl'

const props = defineProps<{
  open: boolean
  defaultCredit: string | null
  /** Adds the link through the API and resolves with the result (including 422 errors). */
  addVideo: (payload: ExternalVideoPayload) => Promise<ItemSaveResult>
}>()

const emit = defineEmits<{
  close: []
  created: [item: OrganizerGalleryItem]
}>()

const { t } = useI18n()

const form = reactive({ url: '', alt_text: '', caption: '', credit: '' })
const saving = ref(false)
const serverErrors = ref<Record<string, string[]>>({})
const duplicate = ref(false)
const touched = ref(false)

watch(
  () => props.open,
  (open) => {
    if (!open) return
    form.url = ''
    form.alt_text = ''
    form.caption = ''
    form.credit = props.defaultCredit ?? ''
    serverErrors.value = {}
    duplicate.value = false
    touched.value = false
  },
  { immediate: true },
)

const parsed = computed(() => parseExternalVideoUrl(form.url))
const providerLabel = computed(() =>
  parsed.value?.provider === ExternalVideoProvider.Vimeo ? t('galleries.tile.vimeo') : t('galleries.tile.youtube'),
)

const urlError = computed<string | null>(() => {
  if (duplicate.value) return t('galleries.link.duplicate')
  if (touched.value && form.url.trim() !== '' && parsed.value === null) return t('galleries.link.unsupported')
  if (touched.value && form.url.trim() === '') return t('common.required_field')
  return serverErrors.value['url']?.[0] ?? null
})

function handleClose(): void {
  if (saving.value) return
  emit('close')
}

async function handleSubmit(): Promise<void> {
  touched.value = true
  duplicate.value = false
  const video = parsed.value
  if (video === null) return

  saving.value = true
  serverErrors.value = {}
  try {
    const result = await props.addVideo({
      url: video.normalizedUrl,
      alt_text: form.alt_text.trim() === '' ? null : form.alt_text.trim(),
      caption: form.caption.trim() === '' ? null : form.caption.trim(),
      credit: form.credit.trim() === '' ? null : form.credit.trim(),
    })
    if (result.item !== null) {
      emit('created', result.item)
      return
    }
    if (result.code === GalleryApiErrorCode.DuplicateExternalVideo) {
      duplicate.value = true
    } else {
      serverErrors.value = result.errors
    }
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <SlideOver :open="open" :title="t('galleries.link.title')" :saving="saving" @close="handleClose">
    <form id="external-video-form" class="space-y-5" novalidate @submit.prevent="handleSubmit">
      <div>
        <label for="link-url" class="block text-sm font-medium text-slate-300 mb-1.5">
          {{ t('galleries.link.url') }}
        </label>
        <input
          id="link-url"
          v-model="form.url"
          type="url"
          inputmode="url"
          :placeholder="t('galleries.link.url_placeholder')"
          class="w-full bg-slate-800 border text-white rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none placeholder:text-slate-500"
          :class="urlError ? 'border-red-500' : 'border-slate-700'"
          :aria-invalid="urlError !== null"
          @blur="touched = true"
        />
        <p v-if="urlError" class="mt-1 text-xs text-red-400" data-testid="link-url-error">{{ urlError }}</p>
      </div>

      <div
        v-if="parsed !== null"
        class="rounded-xl border border-slate-700 bg-slate-900 p-4 flex items-center gap-3"
        data-testid="link-preview"
      >
        <span class="rounded bg-black/70 px-2 py-1 text-xs font-semibold text-white">{{ providerLabel }}</span>
        <p class="text-sm text-slate-300">{{ t('galleries.link.fetch_note') }}</p>
      </div>

      <div>
        <label for="link-alt" class="block text-sm font-medium text-slate-300 mb-1.5">
          {{ t('galleries.item.alt') }}
          <span class="text-slate-500 font-normal">({{ t('common.optional') }})</span>
        </label>
        <input
          id="link-alt"
          v-model="form.alt_text"
          type="text"
          maxlength="300"
          class="w-full bg-slate-800 border text-white rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
          :class="serverErrors['alt_text'] ? 'border-red-500' : 'border-slate-700'"
        />
        <p v-if="serverErrors['alt_text']" class="mt-1 text-xs text-red-400">{{ serverErrors['alt_text'][0] }}</p>
      </div>

      <div>
        <label for="link-caption" class="block text-sm font-medium text-slate-300 mb-1.5">
          {{ t('galleries.item.caption') }}
          <span class="text-slate-500 font-normal">({{ t('common.optional') }})</span>
        </label>
        <input
          id="link-caption"
          v-model="form.caption"
          type="text"
          maxlength="300"
          class="w-full bg-slate-800 border text-white rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
          :class="serverErrors['caption'] ? 'border-red-500' : 'border-slate-700'"
        />
        <p v-if="serverErrors['caption']" class="mt-1 text-xs text-red-400">{{ serverErrors['caption'][0] }}</p>
      </div>

      <div>
        <label for="link-credit" class="block text-sm font-medium text-slate-300 mb-1.5">
          {{ t('galleries.item.credit') }}
          <span class="text-slate-500 font-normal">({{ t('common.optional') }})</span>
        </label>
        <input
          id="link-credit"
          v-model="form.credit"
          type="text"
          maxlength="150"
          class="w-full bg-slate-800 border text-white rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
          :class="serverErrors['credit'] ? 'border-red-500' : 'border-slate-700'"
        />
        <p v-if="serverErrors['credit']" class="mt-1 text-xs text-red-400">{{ serverErrors['credit'][0] }}</p>
      </div>

      <p class="text-xs text-slate-500">{{ t('galleries.link.privacy_note') }}</p>
    </form>

    <template #footer>
      <div class="flex items-center justify-end gap-3">
        <button
          type="button"
          :disabled="saving"
          class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50"
          @click="handleClose"
        >
          {{ t('common.cancel') }}
        </button>
        <button
          type="submit"
          form="external-video-form"
          :disabled="saving"
          class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
        >
          <AppSpinner v-if="saving" size="button" />
          {{ t('galleries.link.add') }}
        </button>
      </div>
    </template>
  </SlideOver>
</template>
